import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArrowPathIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon
} from '@heroicons/react/24/outline'

type Props = {
  src: string
  alt?: string
  className?: string
  imgClassName?: string
  minScale?: number
  maxScale?: number
  initialScale?: number
  zoomStep?: number
  showControls?: boolean
}

const clampScale = (value: number, min: number, max: number) => {
  if (Number.isNaN(value)) return min
  if (value < min) return min
  if (value > max) return max
  return value
}

/**
 * Lightweight CSS-transform based zoom + pan. No external dependency.
 *
 * - Mouse wheel (or trackpad pinch gesture surfaced as wheel + ctrlKey) zooms toward the cursor.
 * - +/- buttons zoom by `zoomStep` (default 0.25) around the centre.
 * - Click-drag pans (only when zoomed in beyond `minScale`).
 * - Double-click and the reset button restore initialScale + zero translation.
 */
export default function ZoomableImage({
  src,
  alt = '',
  className = '',
  imgClassName = '',
  minScale = 1,
  maxScale = 6,
  initialScale = 1,
  zoomStep = 0.25,
  showControls = true
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [scale, setScale] = useState(() => clampScale(initialScale, minScale, maxScale))
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const dragStateRef = useRef<{ startX: number; startY: number; tx: number; ty: number } | null>(null)

  useEffect(() => {
    setScale(clampScale(initialScale, minScale, maxScale))
    setTranslate({ x: 0, y: 0 })
  }, [src, initialScale, minScale, maxScale])

  const reset = useCallback(() => {
    setScale(clampScale(initialScale, minScale, maxScale))
    setTranslate({ x: 0, y: 0 })
  }, [initialScale, minScale, maxScale])

  const zoomTo = useCallback(
    (nextScaleRaw: number, originX?: number, originY?: number) => {
      const node = containerRef.current
      const nextScale = clampScale(nextScaleRaw, minScale, maxScale)
      if (!node) {
        setScale(nextScale)
        return
      }
      const rect = node.getBoundingClientRect()
      const cx = originX != null ? originX - rect.left : rect.width / 2
      const cy = originY != null ? originY - rect.top : rect.height / 2
      setScale((prev) => {
        const ratio = nextScale / prev
        setTranslate((t) => ({
          x: cx - ratio * (cx - t.x),
          y: cy - ratio * (cy - t.y)
        }))
        return nextScale
      })
    },
    [minScale, maxScale]
  )

  const onWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      event.preventDefault()
      const delta = event.deltaY
      const factor = Math.exp(-delta / 320)
      setScale((prev) => {
        const next = clampScale(prev * factor, minScale, maxScale)
        if (next === prev) return prev
        const node = containerRef.current
        if (node) {
          const rect = node.getBoundingClientRect()
          const cx = event.clientX - rect.left
          const cy = event.clientY - rect.top
          const ratio = next / prev
          setTranslate((t) => ({
            x: cx - ratio * (cx - t.x),
            y: cy - ratio * (cy - t.y)
          }))
        }
        return next
      })
    },
    [minScale, maxScale]
  )

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (scale <= minScale) return
      if (event.button !== 0) return
      ;(event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId)
      dragStateRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        tx: translate.x,
        ty: translate.y
      }
      setDragging(true)
    },
    [scale, minScale, translate.x, translate.y]
  )

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragStateRef.current
      if (!drag) return
      const dx = event.clientX - drag.startX
      const dy = event.clientY - drag.startY
      setTranslate({ x: drag.tx + dx, y: drag.ty + dy })
    },
    []
  )

  const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    dragStateRef.current = null
    setDragging(false)
    try {
      ;(event.currentTarget as HTMLDivElement).releasePointerCapture(event.pointerId)
    } catch {
      /* noop */
    }
  }, [])

  const cursor = scale > minScale ? (dragging ? 'grabbing' : 'grab') : 'default'

  return (
    <div className={`relative isolate select-none ${className}`}>
      <div
        ref={containerRef}
        role="img"
        aria-label={alt || 'Zoomable image'}
        data-testid="zoomable-image"
        className="h-full w-full overflow-hidden rounded-md bg-gray-50"
        style={{ cursor, touchAction: 'none' }}
        onWheel={onWheel}
        onDoubleClick={reset}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className={`pointer-events-none mx-auto h-full w-full object-contain ${imgClassName}`}
          style={{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transformOrigin: '0 0',
            transition: dragging ? 'none' : 'transform 60ms linear'
          }}
        />
      </div>
      {showControls && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-1 rounded-md bg-white/90 p-1 shadow-md ring-1 ring-gray-200 backdrop-blur">
          <button
            type="button"
            aria-label="Zoom out"
            className="rounded p-1 text-gray-700 hover:bg-gray-100 disabled:opacity-40"
            disabled={scale <= minScale}
            onClick={() => zoomTo(scale - zoomStep)}
          >
            <MagnifyingGlassMinusIcon className="h-4 w-4" />
          </button>
          <span className="min-w-[2.5rem] text-center text-[11px] font-medium tabular-nums text-gray-700">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            aria-label="Zoom in"
            className="rounded p-1 text-gray-700 hover:bg-gray-100 disabled:opacity-40"
            disabled={scale >= maxScale}
            onClick={() => zoomTo(scale + zoomStep)}
          >
            <MagnifyingGlassPlusIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Reset zoom"
            className="rounded p-1 text-gray-700 hover:bg-gray-100"
            onClick={reset}
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}
